import { CoreBumpRecommendationListingCard, Core_Bump_Recommendation_ListingQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';

export const DELETE_BID_AMOUNT = 0.0;

// Mirrors ProductBumpRate in Core; the minimum follows the
// enforce_1_per_cent_minimum_bump_rate rollout.
const LEGACY_MIN_BUMP_RATE_PERCENT = 0.5;
const MIN_BUMP_RATE_PERCENT = 1;
const MAX_BUMP_RATE_PERCENT = 30;
const BUMP_RATE_INCREMENT_PERCENT = 0.5;

export interface BumpRateLimits {
  minimum: number;
  maximum: number;
  increment: number;
}

export function useBumpRateLimits(): BumpRateLimits {
  const enforceMinimum = useExpEnabled(experiments.ENFORCE_1_PER_CENT_MINIMUM_BUMP_RATE);

  return {
    minimum: enforceMinimum ? MIN_BUMP_RATE_PERCENT : LEGACY_MIN_BUMP_RATE_PERCENT,
    maximum: MAX_BUMP_RATE_PERCENT,
    increment: BUMP_RATE_INCREMENT_PERCENT,
  };
}

// every allowed rate as a percent, from the minimum to the maximum
export function allowedBumpRatePercents(limits: BumpRateLimits): number[] {
  const steps = Math.round((limits.maximum - limits.minimum) / limits.increment);
  return Array.from({ length: steps + 1 }, (_, i) => fixPrecision(limits.minimum + (i * limits.increment)));
}

// Core lets a listing keep a rate below the minimum it was bumped at before
// the minimum was raised, but not return to it once changed. Remove
// currentPercent once no bumps remain below the minimum.
export function isValidBumpRatePercent(
  percent: number,
  limits: BumpRateLimits,
  currentPercent?: number,
): boolean {
  if (!Number.isFinite(percent)) return false;
  if (percent % limits.increment !== 0) return false;
  if (percent > limits.maximum) return false;

  return percent >= limits.minimum || (!!currentPercent && percent === currentPercent);
}

// Relisting a listing kept below the minimum asks the seller to move up to it.
// Sold out listings coming back in stock keep their rate without asking.
const RELIST_STATES_ASKED_TO_MEET_MINIMUM = ['ended', 'ordered'];

export function isRelistBelowMinimumBumpRate(
  listing: {
    state?: string | null;
    relistable?: boolean | null;
    bumpRate?: { rate?: number | null } | null;
  } | null | undefined,
  limits: BumpRateLimits,
): boolean {
  const rate = listing?.bumpRate?.rate;

  return !!listing?.relistable &&
    RELIST_STATES_ASKED_TO_MEET_MINIMUM.includes(listing.state ?? '') &&
    !!rate && rate < limits.minimum;
}

export const BRAND_NEW_CONDITION_SLUGS = Object.freeze([
  'brand-new',
]);

export const USED_CONDITION_SLUGS = Object.freeze([
  'non-functioning',
  'poor',
  'fair',
  'good',
  'very-good',
  'excellent',
  'mint',
  'mint-inventory',
  'b-stock',
]);

export function isBrandNew(conditionSlug: string | null | undefined) {
  return BRAND_NEW_CONDITION_SLUGS.indexOf(conditionSlug ?? '') > -1;
}

export function isCSPMatched(listing: CoreBumpRecommendationListingCard.Fragment) {
  return !!listing.csp?.id;
}

// handle annoying JS math float weirdness resulting from things like:
// 0.07 * 100
// 7.000000000000001
// This will instead now return 7
export function fixPrecision(amount: number): number {
  return Number.parseFloat((amount).toFixed(1));
}

// converts a decimal representation of a "bid"
// to a percent:
// 0.075 returns 7.5
export function formatBid(bid: number): number {
  return fixPrecision(bid * 100);
}

// converts a percent representation of a "bump rate"
// to a "bid" decimal:
// 7.5 returns 0.075
export function bumpRatePercentToDecimal(bumpRate: number): number {
  return bumpRate / 100;
}

export function getCompetitorCount(listing: {
  condition?: { conditionSlug?: string | null } | null;
  csp?: { inventory?: { newTotal?: number | null; usedTotal?: number | null } | null } | null;
}): number {
  if (isBrandNew(listing.condition?.conditionSlug)) {
    return Math.max((listing?.csp?.inventory?.newTotal ?? 0) - 1, 0);
  }

  return Math.max((listing?.csp?.inventory?.usedTotal ?? 0) - 1, 0);
}

export function getInsight(listing: Core_Bump_Recommendation_ListingQuery['listing']) {
  return listing?.featuredListingInsight ?? listing?.featuredListingInsightPreview;
}

export type FeedbackStateKey =
  | 'noBump'
  | 'willIncreaseVisibility'
  | 'visibilityIsIncreased'
  | 'currentlyWinningTopPickNoBump'
  | 'currentlyWinningTopPickUnderRecommendation'
  | 'currentlyWinningTopPick'
  | 'currentlyWinningTopPickOverbid'
  | 'willWinTopPick'
  | 'willWinTopPickOverbid';

export interface FeedbackState {
  key: FeedbackStateKey;
  winner: boolean;
  overbid: boolean;
}

// Picks the feedback message shown under the bump rate selector. Each key maps
// to a complete sentence in discovery.bumpModal.feedbackCallout/feedbackDetails.
export function getFeedbackState({
  currentBidPercent,
  selectedBidPercent,
  recommendedBidPercent,
  isCurrentFeaturedWinner,
}: {
  currentBidPercent: number;
  selectedBidPercent: number;
  recommendedBidPercent: number;
  isCurrentFeaturedWinner?: boolean | null;
}): FeedbackState {
  const meetsRecommendation = selectedBidPercent >= recommendedBidPercent;
  const overbid = selectedBidPercent > recommendedBidPercent;
  // A featured winner keeps the top pick as long as they don't lower their bid,
  // even when the recommendation has since climbed above it.
  const keepsWinningBid = !!isCurrentFeaturedWinner && selectedBidPercent >= currentBidPercent;
  const winner = meetsRecommendation || keepsWinningBid;
  const selectionUnchanged = selectedBidPercent === currentBidPercent;
  const hasExistingBump = currentBidPercent > 0;

  if (!winner) {
    if (selectedBidPercent === 0) {
      return { key: 'noBump', winner, overbid };
    }
    return { key: selectionUnchanged ? 'visibilityIsIncreased' : 'willIncreaseVisibility', winner, overbid };
  }

  if (isCurrentFeaturedWinner) {
    if (!hasExistingBump && !overbid) {
      return { key: 'currentlyWinningTopPickNoBump', winner, overbid };
    }

    if (hasExistingBump && !meetsRecommendation) {
      return { key: 'currentlyWinningTopPickUnderRecommendation', winner, overbid };
    }

    if (selectionUnchanged) {
      return { key: overbid ? 'currentlyWinningTopPickOverbid' : 'currentlyWinningTopPick', winner, overbid };
    }
  }

  return { key: overbid ? 'willWinTopPickOverbid' : 'willWinTopPick', winner, overbid };
}

export function recommendationAmount(
  listing: Core_Bump_Recommendation_ListingQuery['listing'], showTopPickVariant = false,
): number | null {
  if (showTopPickVariant) {
    const insight = getInsight(listing);
    if (insight?.recommendedBumpRate != null) {
      return insight.recommendedBumpRate;
    }
  }
  if (!listing?.bumpRecommendations) return null;
  return fixPrecision(listing.bumpRecommendations.bumpRecommendationAmount);
}

export function isTopPickCandidate(listing: Core_Bump_Recommendation_ListingQuery['listing']): boolean {
  const insight = getInsight(listing);
  return !!insight?.eligibleForTopSpot || !!insight?.featuredWinner;
}

// The rate the bump modal recommends. Anything that logs a recommended rate should
// read it from here so the logged value matches what the seller saw in the modal.
export function getRecommendedBumpRate(listing: Core_Bump_Recommendation_ListingQuery['listing']): number | null {
  return recommendationAmount(listing, isTopPickCandidate(listing));
}
